import { hasValue, styles } from "@repo/helpers";
import { ChevronDown } from "lucide-react";
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../button";
import style from "./style.module.scss";
import { ButtonGroupOption, ButtonGroupProps } from "./types";

export function ButtonGroup(props: ButtonGroupProps) {
	const {
		options,
		selectedId,
		onSelect,
		onButtonClick,
		dropdownArrowLabel = "More options",
		className,
		...buttonProps
	} = props;

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [currentSelectedId, setCurrentSelectedId] = useState(selectedId || options[0]?.id);
	const containerRef = useRef<HTMLDivElement>(null);

	const selectedOption = useMemo(
		() => options.find((option) => option.id === currentSelectedId) || options[0],
		[options, currentSelectedId],
	);

	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener("click", handleClickOutside);

			return () => {
				document.removeEventListener("click", handleClickOutside);
			};
		}
	}, [isDropdownOpen]);

	useEffect(() => {
		if (!hasValue(selectedId)) {
			setCurrentSelectedId(selectedId);
		}
	}, [selectedId]);

	const toggleDropdown = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			setIsDropdownOpen(!isDropdownOpen);
		},
		[isDropdownOpen],
	);

	const handleOptionClick = (e: MouseEvent, option: ButtonGroupOption) => {
		e.stopPropagation();

		setCurrentSelectedId(option.id);
		setIsDropdownOpen(false);

		if (option.onClick) {
			option.onClick(e);
		}

		if (onSelect) {
			onSelect(option.id);
		}
	};

	const handleMainButtonClick = useCallback(
		(e: MouseEvent) => {
			if (selectedOption && onButtonClick) {
				onButtonClick(selectedOption, e);
			}
		},
		[selectedOption, onButtonClick],
	);

	if (!options.length) {
		return null;
	}

	return (
		<div className={styles(style.buttonGroup, className)} ref={containerRef}>
			<Button
				{...buttonProps}
				onClick={handleMainButtonClick}
				disabled={buttonProps.disabled || selectedOption?.disabled}
				className={style.mainButton}
			>
				{selectedOption?.label}
			</Button>

			<Button
				{...buttonProps}
				onClick={toggleDropdown}
				className={style.dropdownButton}
				title={dropdownArrowLabel}
				disabled={buttonProps.disabled}
			>
				<ChevronDown className={style.chevronIcon} />
			</Button>

			<div className={styles(style.dropdownContent, isDropdownOpen && style.show)}>
				{options.map((option) => (
					<button
						key={option.id}
						className={styles(
							style.dropdownItem,
							option.id === currentSelectedId && style.selected,
							option.disabled && style.disabled,
						)}
						disabled={option.disabled}
						onClick={(e) => handleOptionClick(e, option)}
					>
						{option.label}
					</button>
				))}
			</div>
		</div>
	);
}

export * from "./types";
